import { ExtendedBooking } from '@wix/ambassador-bookings-reader-v2-extended-booking/types';
import { CompletionRequirement } from '@wix/ambassador-bookings-services-v2-service/types';
import { ResourceSelection } from '@wix/ambassador-bookings-v2-booking/types';
import type { ListEligibleMembershipsResponse } from '@wix/ambassador-ecom-memberships-spi-host-v1-membership/types';
import {
  IntakeFormSubmission,
  Status,
} from '@wix/ambassador-intake-forms-v1-intake-form-submission/types';
import { Member } from '@wix/ambassador-members-ng-api/types';
import { BookingsLineItemOption } from '@wix/bookings-checkout-api';
import { FormOverrides, FormValues } from '@wix/form-viewer';
import { GetActiveFeaturesResponse } from '../../types/ambassador/bookings/ambassador-services-catalog';
import { CouponInfo } from '../../types/coupons';
import { DynamicPriceInfo } from '../../types/dynamicPrice';
import { FormError } from '../../types/errors';
import { FormStatus } from '../../types/form-state';
import {
  FormNestedSlot,
  FormSelectedSlot,
} from '../../types/formSelectedSlots';
import type {
  BusinessInfo,
  CartModal,
  Dialog,
  PaymentMethod,
  PaymentOption,
  PlanInfoWithDescription,
  PlanWithDescription,
  ServicePaymentDetails,
  SettingsSubTab,
  SettingsTab,
  SummaryPaymentDetails,
} from '../../types/types';
import { FormService } from '../mappers/serviceMapper/service.mapper';

export type EditorContext = {
  isDummy: boolean;
  selectedSettingsTabId?: SettingsTab;
  selectedSettingsSubTabId?: SettingsSubTab;
};

export type FormState = {
  publicPlans?: PlanWithDescription[];
  eligiblePlans?: PlanInfoWithDescription[][];
  isPricingPlanInstalled: boolean;
  isMemberAreaInstalled: boolean;
  isUpsellPluginInstalled: boolean;
  isCart?: boolean;
  isServiceInCart?: boolean;
  couponInfo: CouponInfo;
  businessInfo: BusinessInfo;
  activeFeatures: GetActiveFeaturesResponse;
  memberDetails?: Member;
  errors: FormError[];
  editorContext: EditorContext;
  status: FormStatus;
  dialog?: Dialog;
  isDayful?: boolean;
  wixForms: WixForms;
  selectedNumberOfParticipants: number;
  cartModal?: CartModal;
  bookingsLineItemOptions?: BookingsLineItemOption[];
  formSelectedSlot: FormSelectedSlot;
  serviceData: ServiceData;
  existingBookingDetails?: ExtendedBooking;
  isSMSAutomationsEnabled?: boolean;
  intakeForms?: IntakeForms;
  isSlotUnavailable?: boolean;
};

export type WixForms = {
  initialValues: FormValues;
  overrides: FormOverrides;
  fieldProperties: WixFormsFieldProperties;
};

export interface WixFormsFieldProperties {
  [key: string]: {
    required: boolean;
    hidden: boolean;
    fieldType: string;
  };
}

export type IntakeForms = Record<string, IntakeForm>;

export type IntakeFormInfoMap = Record<string, IntakeFormInfo>;

export type IntakeForm = {
  formId: string;
  formName?: string;
  completionRequirement: CompletionRequirement;
  status: IntakeFormStatus;
  formData?: any;
  isLoading: boolean;
  previousSubmissionStatus?: Status;
};

export enum IntakeFormStatus {
  COMPLETED = 'completed',
  REQUIRED = 'required',
  OPTIONAL = 'optional',
}

export enum IntakeFormInfoType {
  VISITOR = 'visitorData',
  CONTACT = 'contactData',
}

export type ServiceData = {
  isSingleService: boolean;
  formId: string;
  summaryPaymentDetails: SummaryPaymentDetails;
  maxNumberOfParticipants: number;
  maxNumberOfParticipantsWithoutPP?: number;
  slotServices: SlotServices; // key is lineItemId
};

export type IntakeFormInfo = {
  formName: string;
  mostRecentSubmission?: IntakeFormSubmission;
};

export type SlotServices = { [key: string]: SlotService }; // key is lineItemId

export type SlotService = {
  memberships?: ListEligibleMembershipsResponse;
  possiblePlans?: PlanWithDescription[];
  eligiblePlans?: PlanInfoWithDescription[];
  selectedPaymentOption: PaymentOption;
  selectedPaymentType: PaymentMethod;
  service: FormService;
  nestedSlot: FormNestedSlot;
  paymentDetails: ServicePaymentDetails;
  dynamicPriceInfo?: DynamicPriceInfo;
  addOnByGroup?: SlotAddOnGroup[];
  selectedAddons?: SelectedAddons;
  resourceSelections?: ResourceSelection[];
};

export interface SelectedAddonInfo {
  id: string;
  quantity: number;
  durationInMinutes?: number;
  price?: string | null;
  currency?: string | null;
}

export interface SelectedAddons {
  [groupId: string]: {
    [addonId: string]: SelectedAddonInfo;
  };
}

export type SlotAddOnGroup = {
  id?: string | null;
  name?: string | null;
  prompt?: string | null;
  maxNumberOfAddOns?: number | null;
  addOns?: SlotAddOn[] | null;
};

export type SlotAddOn = {
  id?: string | null;
  name?: string | null;
  duration?: number | null;
  price?: string | null;
  currency?: string | null;
  maxQuantity?: number | null;
};

export type ServiceAddOnGroup = {
  id?: string;
  name?: string;
  addOnIds?: string[];
};
